import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Download, FileDown } from 'lucide-react';
import legacyUapiService from '../../services/LegacyUapiService';
import { formatDateTime } from '../../utils/datetime';
import Button from './Button';
import Spinner from './Spinner';
import CopyButton from './CopyButton';
import Tabs from './Tabs';
import '../PaymentPorterPage.css';   // .pp-rules-add-btn, .pp-criteria-add-btn

// The Payload tab for a MODULE: the message it carries, as JSON Schema.
//
// A MODULE, NOT A ROUTE. Oracle publishes one spec per module — act.json, rsv.json — and the message is
// the module's, so this tab exists on /act and not on the routes beneath it. /act/v1 does not carry a
// different message; it is a version of the same one.
//
// ONE DOCUMENT, NOT A CATALOGUE. The spec states that message on each of its GET operations, so the
// import fetches the file, extracts them, and asks which one IS the message. What is stored and shown is
// that single JSON Schema — which operations exist is the route tree's job, and duplicating it here made
// the tab an operation list with a payload buried in it.
//
// THE GET SCHEMA IS THE RESOURCE. OHIP describes a resource once: what a GET returns is what a POST or
// PUT of the same resource sends, so the one schema serves all three.
export default function UapiRoutePayload({ route, readOnly = false }) {
    // The module's message — ONE document. Fetched with its schema: a single payload is tens of
    // kilobytes, and deferring it only to fetch it on the next click buys nothing.
    const [message, setMessage] = useState(null);
    const [specFile, setSpecFile] = useState('');
    const [loading, setLoading] = useState(true);
    const [importing, setImporting] = useState(false);
    const [downloading, setDownloading] = useState(false);
    // The choose-the-message step: what the spec publishes, and which one is selected.
    const [preview, setPreview] = useState(null);
    // Which of the spec's GET payloads IS the module's message. One, not a set.
    const [picked, setPicked] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);

    const [view, setView] = useState('schema');

    const errText = (e, fallback) =>
        e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || fallback;

    const load = useCallback(async () => {
        if (!route?.id) return;
        try {
            setLoading(true);
            setError(null);
            const res = await legacyUapiService.listRoutePayloads(route.id);
            const d = res.data || res;
            setSpecFile(d.route?.spec_file || '');
            const row = (d.payloads || [])[0] || null;
            if (!row) { setMessage(null); return; }
            const full = await legacyUapiService.getPayload(row.id);
            setMessage(full.data || full);
            setView('schema');
        } catch (e) {
            setError(errText(e, 'Could not load the payload definitions'));
        } finally {
            setLoading(false);
        }
    }, [route?.id]);

    useEffect(() => { load(); }, [load]);

    // Step one: fetch the spec and show what is in it. Nothing is written.
    const openPreview = async () => {
        setImporting(true);
        setError(null);
        setNotice(null);
        try {
            const res = await legacyUapiService.previewRoutePayloads(route.id, specFile.trim() || undefined);
            const d = res.data || res;
            setPreview(d);
            setSpecFile(d.specFile || specFile);
            // Everything already imported starts ticked, and so does everything new: the common case is
            // "take the lot", and the reader unticks what this route should not carry.
            // Pre-selected with what is already stored, else the first published operation — the
            // shortest path, which is the module's collection more often than not.
            setPicked(message?.operation_path || (d.operations || [])[0]?.operation_path || null);
        } catch (e) {
            setError(errText(e, 'Could not read that spec'));
        } finally {
            setImporting(false);
        }
    };

    // Step two: import exactly what is ticked.
    const runImport = async () => {
        if (!picked) return;
        setImporting(true);
        setError(null);
        try {
            // Every published operation's body is stored; the ticked one is designated the module's
            // message. Storing only the message would leave the Operations tab with nothing to show.
            const all = (preview?.operations || []).map(o => o.operation_path);
            const res = await legacyUapiService.importRoutePayloads(
                route.id, specFile.trim() || undefined, all, picked);
            const d = res.data || res;
            setSpecFile(d.specFile || specFile);
            setPreview(null);
            setPicked(null);
            setNotice(`Imported ${d.imported} payload${d.imported === 1 ? '' : 's'} from ${d.specFile}`
                + `; message set to ${d.message || picked}`
                + `${d.specVersion ? ` (${d.specTitle || 'spec'} ${d.specVersion})` : ''}.`);
            await load();
        } catch (e) {
            setError(errText(e, 'Import failed'));
        } finally {
            setImporting(false);
        }
    };

    // Escape closes it, as it does on every other dialog in the portal. Not while the import is in
    // flight: the request would land anyway and the screen would be showing a stale list.
    useEffect(() => {
        if (!preview) return undefined;
        const onKey = (e) => { if (e.key === 'Escape' && !importing) { setPreview(null); setPicked(null); } };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [preview, importing]);



    // Pretty-printed on the way out rather than stored that way: the column holds the document, and how
    // it is laid out is a display choice.
    // The module as a spec file, saved locally.
    //
    // Fetched rather than linked: the admin API needs an Authorization header, which a plain <a href>
    // cannot send. The object URL is revoked straight after the click — one left behind pins the whole
    // document in memory for the life of the tab, and these run to megabytes.
    const downloadSpec = async () => {
        setDownloading(true);
        setError(null);
        try {
            const res = await legacyUapiService.getRouteOpenApi(route.id);
            const d = res.data || res;
            const name = `${String(route.path).replace(/^\//, '').replace(/\//g, '-') || 'module'}.json`;
            const url = URL.createObjectURL(
                new Blob([JSON.stringify(d.spec, null, 2)], { type: 'application/json' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = name;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            const st = d.stats || {};
            setNotice(`Downloaded ${name} - ${st.operations} operation(s), ${st.definitions} definition(s)`
                + `${st.collisions && st.collisions.length ? `, ${st.collisions.length} definition clash(es) kept at first import` : ''}.`);
        } catch (e) {
            setError(errText(e, 'Could not build the spec for this module'));
        } finally {
            setDownloading(false);
        }
    };

    const pretty = (text) => {
        try { return JSON.stringify(JSON.parse(text), null, 2); } catch { return text; }
    };

    const shown = message && (view === 'sample' ? message.sample_json : message.schema_json);

    return (
        <div className="lu-payload">
            <div className="lu-payload-bar">
                <div className="lu-payload-spec">
                    <label className="lu-cred-label" htmlFor="luSpecFile">Oracle spec file</label>
                    <input
                        id="luSpecFile"
                        className="form-input"
                        value={specFile}
                        onChange={(e) => setSpecFile(e.target.value)}
                        placeholder={`${String(route?.path || '').replace(/^\//, '')}.json`}
                        disabled={readOnly || importing}
                    />
                    <span className="lu-payload-hint">
                        One file per module in Oracle's rest-api-specs/property/v1. Usually named after the
                        route — not always: /ari is ars.json.
                    </span>
                </div>
                <div className="lu-payload-actions">
                    {/* Out as well as in: this module as a Swagger 2.0 document, the same shape as the
                        files Oracle publishes. */}
                    <Button variant="secondary" onClick={downloadSpec} disabled={downloading || !route?.id}>
                        <FileDown className="icon" /> {downloading ? 'Building…' : 'Download spec'}
                    </Button>
                    {!readOnly && (
                        <Button variant="secondary" onClick={openPreview} disabled={importing || !route?.id}>
                            <Download className="icon" /> {importing ? 'Reading…' : 'Import from spec'}
                        </Button>
                    )}
                </div>
            </div>

            {error && (
                <div className="error-alert">
                    <div className="error-alert-content">
                        <AlertCircle className="error-alert-icon" />
                        <p className="error-alert-message">{error}</p>
                    </div>
                </div>
            )}
            {notice && <p className="lu-payload-notice">{notice}</p>}

            {/* Chosen before anything is written. A dialog, deliberately: the list behind it is what the
                import adds to, and it stays in view. */}
            {preview && (
                <div className="lu-import-backdrop" onClick={() => !importing && setPreview(null)}>
                    <div className="lu-import-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="lu-import-head">
                            <h3 className="lu-import-title">
                                Choose {route?.path}&apos;s message from {preview.specFile}
                            </h3>
                            <p className="lu-import-sub">
                                {preview.specTitle || 'The spec'}
                                {preview.specVersion ? ` ${preview.specVersion}` : ''} states this module's
                                message on each of its {preview.operations.length} GET operations. Pick the one
                                whose body IS the message — its schema is what a POST or PUT of the same
                                resource sends too. Nothing has been written yet.
                            </p>

                        </div>

                        <div className="lu-import-list">
                            {preview.operations.map((o) => {
                                // The one currently stored, so re-importing is visibly a refresh of the
                                // same choice rather than a new one.
                                const already = message?.operation_path === o.operation_path;
                                return (
                                    <label
                                        key={o.operation_path}
                                        className={`lu-import-row${picked === o.operation_path ? ' picked' : ''}`}
                                    >
                                        <input
                                            type="radio"
                                            name="uapi-message"
                                            checked={picked === o.operation_path}
                                            onChange={() => setPicked(o.operation_path)}
                                            disabled={importing}
                                        />
                                        <span className="lu-import-row-main">
                                            <span className="lu-import-op">
                                                {o.operation_path}
                                                {already && <span className="lu-import-already">current</span>}
                                            </span>
                                            {o.summary && <span className="lu-import-summary">{o.summary}</span>}
                                        </span>
                                        <span className="lu-import-meta">
                                            {String(o.applies_to || '').split(',').filter(Boolean).map(m => (
                                                <span key={m} className={`lu-method lu-method-${m.toLowerCase()}`}>{m}</span>
                                            ))}
                                            <span className="lu-import-size">
                                                {Math.max(1, Math.round((o.schema_bytes || 0) / 1024))} KB
                                            </span>
                                        </span>
                                    </label>
                                );
                            })}
                        </div>

                        <div className="lu-import-footer">
                            <button
                                type="button"
                                className="pp-criteria-add-btn"
                                onClick={() => { setPreview(null); setPicked(null); }}
                                disabled={importing}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="pp-rules-add-btn"
                                onClick={runImport}
                                disabled={importing || !picked.length}
                            >
                                {importing ? 'Importing…' : 'Use as the message'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="loading-section"><Spinner size="md" /><p>Loading payload...</p></div>
            ) : !message ? (
                <p className="lu-empty">
                    No payload defined for {route?.path} yet. Import it from Oracle's published spec above
                    — the module's message, as OHIP defines it, expressed as JSON Schema.
                </p>
            ) : (
                <div className="lu-payload-doc">
                    <div className="lu-payload-doc-bar">
                        <Tabs
                            size="sm"
                            className="lu-doc-tabs"
                            active={view}
                            onChange={setView}
                            tabs={[
                                {
                                    key: 'schema',
                                    label: 'JSON Schema',
                                    icon: (
                                        <svg viewBox="0 0 20 20" fill="none">
                                            <path d="M7.5 4H6a2 2 0 0 0-2 2v2.5L2.5 10 4 11.5V14a2 2 0 0 0 2 2h1.5M12.5 4H14a2 2 0 0 1 2 2v2.5L17.5 10 16 11.5V14a2 2 0 0 1-2 2h-1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ),
                                },
                                // Only when the spec published one — an absent example is not an empty
                                // tab to click into.
                                {
                                    key: 'sample',
                                    label: 'Example',
                                    hidden: !message.sample_json,
                                    icon: (
                                        <svg viewBox="0 0 20 20" fill="none">
                                            <rect x="3.5" y="4" width="13" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.6"/>
                                            <path d="M3.5 7.5h13M6.5 11h7M6.5 13.5h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                                        </svg>
                                    ),
                                },
                            ]}
                        />
                        <div className="lu-payload-doc-actions">
                            <a
                                className="lu-payload-source"
                                href={message.spec_url}
                                target="_blank"
                                rel="noreferrer"
                                title={`${message.operation_path} — imported ${formatDateTime(message.imported_at)}`}
                            >
                                {message.spec_file}
                                {message.spec_version ? ` · ${message.spec_version}` : ''}
                            </a>
                            <CopyButton value={pretty(shown)} label="Copy" className="copy-btn-inline" />
                        </div>
                    </div>
                    <pre className="lu-payload-json">{pretty(shown)}</pre>
                </div>
            )}
        </div>
    );
}
